// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import {
  withStyles,
  Grid,
  Button,
} from '@material-ui/core';
import { isArray, includes, some, get } from 'lodash';
import PropTypes from 'prop-types';

import {
  FileDropzone,
  HelpContainer,
  AlertMessages,
  FormActionButtons,
  Loading,
  ErrorContainer,
} from '../../../components';
import { saveBundleAssets } from '../../../actions/gatewayBundle';
import { getUserDetails } from '../../../reducers/portalConfig';
import {
  getIsLoading,
  getIsError,
  getErrors,
  getIsBundleSaveSuccess,
  getGatewayBundleDetails,
} from '../../../reducers/gatewayBundle';
import { getBundlesExtension } from '../../../utils';
import { getI18n, getI18nFormattedMessage } from '../../../utils/intl';
import {
  HELP_ITEMS,
  BUNDLE_FILE_TYPE,
  YML_FILE_EXT,
  BUNDLE_FILE_EXT,
  DELETE_BUNDLE_FILE_EXT,
  MAX_FILE_SIZE,
  MIME_TYPES,
  GRAPHMAN_BUNDLE_FILE_EXT,
  BUNDLE_FORMAT_RESTMAN,
  BUNDLE_FORMAT_GRAPHMAN,
  RESTMAN_ALLOWED_FILE_EXTENSIONS,
} from './constants';
import { ALERT_ERROR } from '../../../constants';
import styles from './styles';
import AlertDialog from "../../../components/AlertDialog";
import { BreadcrumbsForPages } from "../../../pageComponents";
import { GATEWAY_BUNDLES_DETAILS } from "../../../pageComponents/BreadcrumbsForPages/constants";

export const GatewayBundleEdit = (props) => {
  const {
    classes,
    userContext,
    assets = [],
    isLoading,
    bundleErrors,
    isError,
    isBundleSaveSuccess,
    gatewayBundleDetails,
    pathName,
   } = props;

  const intl = getI18n(useIntl());
  const acceptFileTypes = BUNDLE_FILE_TYPE;
  const [bundleFiles, setBundleFiles] = useState({
    files: assets,
    acceptFileTypes,
    maxFiles: 3,
    error: false,
    helperText: getI18nFormattedMessage('label.gateway.bundle.file.help'),
    bundleFormat: BUNDLE_FORMAT_RESTMAN,
  });
  const [isFileValidation, setIsFileValidation] = useState(false);
  const [fileValidationMessage, setFileValidationMessage] = useState('');
  const [errors, setErrors] = useState([]);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const breadcrubItems = [...GATEWAY_BUNDLES_DETAILS,
    ...[ { isActive: false, link: '', linkName: 'add' }]];
  localStorage.setItem('isSaveBundleAssets', false);

  const notifyMessages = (message, status) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  const clearErrorMessages = () => {
    setBundleFiles({ ...bundleFiles, error: false });
    setIsFileValidation(false);
    notifyMessages('', '');
    setErrors([]);
  };

  useEffect(() => {
    if (!userContext.portalAdmin) { props.push('/404'); }
  }, [userContext]);

  useEffect(() => {
    if (isError) {
      notifyMessages(
        <>
          {intl.getI18nMessage('label.gateway.bundle.add.error.message')}
          <Button
            onClick={() => setAlertDialogOpen(true)}
            className={classes.alertMessagesButton}>
            {intl.getI18nMessage('label.more.info')}
          </Button>
        </>, ALERT_ERROR);
      setErrors(bundleErrors);
    } else {
      clearErrorMessages();
    }
  }, [isError, bundleErrors]);

  useEffect(() => {
    if (isBundleSaveSuccess) {
      clearErrorMessages();
      localStorage.setItem('isSaveBundleAssets', true);
      props.push(`/publish/gateway-bundles/details/${gatewayBundleDetails.uuid}`);
    }
  }, [isBundleSaveSuccess, gatewayBundleDetails]);

  const setBundleFormat = (file) => {
    const extension = getBundlesExtension(file.name, RESTMAN_ALLOWED_FILE_EXTENSIONS);
    if(bundleFiles.files.length > 0) {
      // Clear any existing files if uploaded file is of a different bundle format.
      resetBundleFiles(extension);
    } else {
      if(extension === GRAPHMAN_BUNDLE_FILE_EXT) {
        bundleFiles.bundleFormat = BUNDLE_FORMAT_GRAPHMAN
      } else {
        bundleFiles.bundleFormat = BUNDLE_FORMAT_RESTMAN
      }
    }
  }

  const resetBundleFiles = (extension) => {
    const hasRestmanFiles = some(bundleFiles.files, bundleFile =>
        RESTMAN_ALLOWED_FILE_EXTENSIONS.includes(bundleFile.ext));
    const hasGraphmanFile = some(bundleFiles.files, bundleFile =>
        GRAPHMAN_BUNDLE_FILE_EXT === bundleFile.ext);
    if(extension === GRAPHMAN_BUNDLE_FILE_EXT && hasRestmanFiles) {
      bundleFiles.files = [];
      bundleFiles.bundleFormat = BUNDLE_FORMAT_GRAPHMAN;
      bundleFiles.maxFiles = 1;
    }
    if(RESTMAN_ALLOWED_FILE_EXTENSIONS.includes(extension) && hasGraphmanFile) {
      bundleFiles.files = [];
      bundleFiles.bundleFormat = BUNDLE_FORMAT_RESTMAN;
      bundleFiles.maxFiles = 3;
    }
  }

  const hasValidateFile = (file) => {
    const isGraphmanFile = bundleFiles.bundleFormat === BUNDLE_FORMAT_GRAPHMAN;
    const MAX_NUM_FILES = isGraphmanFile ? 1 : 3;
    // Common validations across Graphman and Restman Bundle Files.
    if (file.size > MAX_FILE_SIZE) {
      bundleFiles.error = true;
      setIsFileValidation(true);
      setFileValidationMessage(getI18nFormattedMessage('label.error.upload.file.size'));
      return false;
    }
    if (some(bundleFiles.files, { name: file.name })) {
      setIsFileValidation(true);
      setFileValidationMessage(getI18nFormattedMessage('error.gateway.bundle.file.name.unique'));
      return false;
    }
    if (bundleFiles.files.length === MAX_NUM_FILES) {
      setIsFileValidation(true);
      setFileValidationMessage(getI18nFormattedMessage('error.gateway.bundle.file.count', { MAX_NUM_FILES }));
      return false;
    }
    // Exclusive Validations
    if(isGraphmanFile) {
      return validateGraphmanFile(file);
    } else {
      return validateRestmanFile(file);
    }
  };

  const validateGraphmanFile = (file) => {
    const extension = getBundlesExtension(file.name)
    if (extension !== GRAPHMAN_BUNDLE_FILE_EXT) {
      bundleFiles.error = true;
      return false;
    }
    setIsFileValidation(false);
    return true;
  }

  const validateRestmanFile = (file) => {
    const extension = getBundlesExtension(file.name, RESTMAN_ALLOWED_FILE_EXTENSIONS);
    if (!includes(RESTMAN_ALLOWED_FILE_EXTENSIONS, extension)) {
      bundleFiles.error = true;
      return false;
    }
    const filterYmlFiles = bundleFiles.files.filter((obj) =>
      obj.ext === YML_FILE_EXT);
    if (filterYmlFiles.length === 1 && extension === YML_FILE_EXT) {
      setIsFileValidation(true);
      setFileValidationMessage(getI18nFormattedMessage('error.gateway.bundle.file.metadata.yml.count'));
      return false;
    }
    const filterBundleFiles = bundleFiles.files.filter((obj) =>
      obj.ext === BUNDLE_FILE_EXT);
    if (filterBundleFiles.length === 1 && extension === BUNDLE_FILE_EXT) {
      setIsFileValidation(true);
      setFileValidationMessage(getI18nFormattedMessage('error.gateway.bundle.file.bundle.count'));
      return false;
    }

    const filterDeleteBundleFiles = bundleFiles.files.filter((obj) =>
      obj.ext === DELETE_BUNDLE_FILE_EXT);
    if (filterDeleteBundleFiles.length === 1 && extension === DELETE_BUNDLE_FILE_EXT) {
      setIsFileValidation(true);
      setFileValidationMessage(getI18nFormattedMessage('error.gateway.bundle.file.delete.bundle.count'));
      return false;
    }
    setIsFileValidation(false);
    return true;
  };

  const fileLoad = (file) => {
    const reader = new FileReader();
    reader.onabort = () => {
      setBundleFiles({ ...bundleFiles, error: true });
    };
    reader.onerror = () => {
      setBundleFiles({ ...bundleFiles, error: true });
    };
    reader.onload = () => {
      // Set the bundle format based on the type of uploaded file.
      setBundleFormat(file);
      if (hasValidateFile(file)) {
        bundleFiles.error = false;
        const fileContent = reader.result;
        const fileExtenstion = getBundlesExtension(file.name, RESTMAN_ALLOWED_FILE_EXTENSIONS);
        const fileData = file;
        fileData.ext = fileExtenstion;
        fileData.blob = new Blob(
          [fileContent],
          { type: MIME_TYPES[fileExtenstion] },
        );
        bundleFiles.files.push(fileData);
        setBundleFiles({ ...bundleFiles });
      } else {
        setBundleFiles({ ...bundleFiles });
      }
    };
    reader.readAsText(file);
  };

  const handleFiles = (files) => {
    clearErrorMessages();
    if (isArray(files)) {
      files.forEach((file) => {
        fileLoad(file);
      });
    } else {
      fileLoad(files);
    }
  };

  const onRemoveFile = (file) => {
    clearErrorMessages();
    const remainBundleFiles = bundleFiles.files.filter((obj) => obj.name !== file.name);
    setBundleFiles({ ...bundleFiles, files: remainBundleFiles });
  };

  const onCancel = () => {
    clearErrorMessages();
    window.location.href = '/publish/gateway-bundles/';
  };

  const hasValidSaveButton = (selectedFiles) => {
    if(bundleFiles.bundleFormat === BUNDLE_FORMAT_RESTMAN) {
      if (selectedFiles.files.length !== 3) {
        return false;
      }
      const filterYmlFiles = selectedFiles.files.filter((obj) =>
        obj.ext === YML_FILE_EXT);
      const filterBundleFiles = selectedFiles.files.filter((obj) =>
        obj.ext === BUNDLE_FILE_EXT);
      const filterDeleteBundleFiles = bundleFiles.files.filter((obj) =>
        obj.ext === DELETE_BUNDLE_FILE_EXT);
      if (filterYmlFiles.length === 1 && filterBundleFiles.length === 1
        && filterDeleteBundleFiles.length === 1) {
        return true;
      }
    }
    return bundleFiles.bundleFormat === BUNDLE_FORMAT_GRAPHMAN &&
        selectedFiles.files.length === 1 &&
        selectedFiles.files.filter((obj) => obj.ext === GRAPHMAN_BUNDLE_FILE_EXT).length === 1;
  };

  const onNext = async () => {
    clearErrorMessages();
    if (hasValidSaveButton(bundleFiles)) {
      await props.saveBundleAssets(bundleFiles.files);
    } else {
      setBundleFiles({ ...bundleFiles, error: true });
      setNotificationStatus('error');
      setNotificationMessage(getI18nFormattedMessage('error.gateway.bundle.file.size'));
    }
  };

  const RESTMAN_FILE_TITLES = [
    intl.getI18nMessage('label.gateway.bundle.selected.file.metadata.yml.title'),
    intl.getI18nMessage('label.gateway.bundle.selected.file.bundle.title'),
    intl.getI18nMessage('label.gateway.bundle.selected.file.delete.bundle.title'),
  ]

  const GRAPHMAN_FILE_TITLE = [
      intl.getI18nMessage('label.gateway.bundle.selected.file.graphman.bundle.title'),
  ]

  return (
    <div
      className={classes.content}
      id="gateway-bundle-edit-page"
      data-apim-test="gateway-bundle-edit-page"
    >
      <AlertDialog
        isOpen={alertDialogOpen}
        title={intl.getI18nMessage('warning.status')}
        description={errors.length > 0 && <ErrorContainer errors={errors} />}
        submitText={intl.getI18nMessage('label.ok.button')}
        cancelText={intl.getI18nMessage('label.cancel.button')}
        onClose={() => setAlertDialogOpen(false)}
        onSubmit={() => setAlertDialogOpen(false)}
        onCancel={() => setAlertDialogOpen(false)}
        dialogId={`gateway-bundle-edit-action-dialog`}
      />
      {isLoading && <Loading pageLoader /> }
      <Grid container className={classes.gridContainer}>
        <Grid
          md={2}
          item
          className={classes.leftSideBar}
          id="gateway-bundle-edit-left-sidebar"
          data-apim-test="gateway-bundle-edit-left-sidebar"
        />
        <Grid
          md={7}
          item
          className={classes.mainContent}
          id="gateway-bundle-edit-main-content"
          data-apim-test="gateway-bundle-edit-main-content"
        >
          <BreadcrumbsForPages pageContainerClass={classes.pageBreadCrumbClass}
               pathName={pathName} items={breadcrubItems} />
          {notificationMessage &&
              <AlertMessages
                id="gateway-bundle-edit-notification"
                data-apim-test="gateway-bundle-edit-notification"
                message={notificationMessage}
                variant={notificationStatus}
                hasAction={true}
                hasNotificationIcon
                onClose={() => {
                  setNotificationMessage('');
                  setNotificationStatus('');
                }}
                containerClass={classes.alertMessages}
                actionButtonClass={classes.alertMessagesButton}
              />
          }

          <h1 className={classes.pageTitle}> Add Gateway Bundles</h1>
          <FileDropzone
            handleFiles={handleFiles}
            selectedFiles={bundleFiles}
            helperText={''}
            title={''}
            id={'gateway-bundle-edit-file-upload'}
            allowedFileExtentions={bundleFiles.bundleFormat === BUNDLE_FORMAT_RESTMAN ?
                RESTMAN_ALLOWED_FILE_EXTENSIONS : [GRAPHMAN_BUNDLE_FILE_EXT]}
            listFileTitles={bundleFiles.bundleFormat === BUNDLE_FORMAT_RESTMAN ?
                RESTMAN_FILE_TITLES : GRAPHMAN_FILE_TITLE}
            onRemoveFile={onRemoveFile}
            isValidation={isFileValidation}
            validationMessage={fileValidationMessage}
            defaultUploaderText={intl.getI18nMessage('label.gateway.bundle.file.uploader')}
            isMultipleTitle
            showSelectedFiles={bundleFiles.files.length > 0}
            showFilesHeader={true}
          />
        </Grid>
        <Grid
          md={3}
          item
          className={classes.rightSideBar}
          id="gateway-bundle-edit-right-sidebar"
          data-apim-test="gateway-bundle-edit-right-sidebar"
        >
          <HelpContainer
            helpItems={HELP_ITEMS.bundleHelp}
          />
        </Grid>
      </Grid>
      <FormActionButtons
        onNextClick={onNext}
        onCancelClick={onCancel}
        nextText={intl.getI18nMessage('label.save.button')}
        cancelText={intl.getI18nMessage('label.exit.button')}
        id="bundle-form-buttons"
      />
    </div>
  );
};

const mapStateToProps = state => ({
  isError: getIsError(state),
  isLoading: getIsLoading(state),
  bundleErrors: getErrors(state),
  isBundleSaveSuccess: getIsBundleSaveSuccess(state),
  gatewayBundleDetails: getGatewayBundleDetails(state),
  userContext: getUserDetails(state),
  pathName: get(state, 'router.location.pathname'),
});

const mapDispatchToProps = {
  saveBundleAssets,
  push,
};

GatewayBundleEdit.propTypes = {
  classes: PropTypes.object,
  assets: PropTypes.object,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  isBundleSaveSuccess: PropTypes.bool,
  bundleErrors: PropTypes.arrayOf(PropTypes.object),
  saveBundleAssets: PropTypes.func,
  userContext: PropTypes.object,
  gatewayBundleDetails: PropTypes.object,
  push: PropTypes.object,
  pathName: PropTypes.string,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(GatewayBundleEdit);
